import { useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { selectCrossClientLandingSelectedOwnerId } from '../../../../store/crossClient/crossClientLanding'
import { useGetCrossClientQueryExecutionsQuery } from '../../../../store/crossClient/crossClient'
import { type CrossClientQueryExecution, CrossClientQueryPhases } from '../../../../models/CrossClient'

const PAGE_SIZE = 10

export interface CrossClientCompletedResult {
  executions: CrossClientQueryExecution[]
  totalCount: number
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  showMore: () => void
}

export const useCrossClientCompleted = (): CrossClientCompletedResult => {
  const accountId = useAppSelector(selectAccountId)
  const selectedOwnerId = useAppSelector(selectCrossClientLandingSelectedOwnerId)
  const createdBy = selectedOwnerId ?? undefined

  // Page size is scoped to the owner it was expanded for. Deriving the effective size (rather than
  // resetting via a render-phase setState or an effect) keeps a new owner at PAGE_SIZE while avoiding
  // the cascading-render pitfalls that break react-redux's store subscription.
  const [paging, setPaging] = useState({ ownerId: selectedOwnerId, size: PAGE_SIZE })
  const size = paging.ownerId === selectedOwnerId ? paging.size : PAGE_SIZE

  const { data, isLoading, isFetching } = useGetCrossClientQueryExecutionsQuery(
    {
      accountId,
      phases: [CrossClientQueryPhases.COMPLETED],
      hasUnactionedMatches: false,
      fullyAnalyzed: true,
      expand: ['matches'],
      createdBy,
      pageRequest: { page: 0, size },
    },
    { refetchOnMountOrArgChange: true },
  )

  const executions = data?.data ?? []
  const totalCount = data?.page.totalElements ?? 0

  return {
    executions,
    totalCount,
    loading: isLoading,
    hasMore: executions.length < totalCount,
    loadingMore: isFetching,
    showMore: () =>
      setPaging((prev) => {
        const base = prev.ownerId === selectedOwnerId ? prev : { ownerId: selectedOwnerId, size: PAGE_SIZE }
        return { ownerId: selectedOwnerId, size: base.size + PAGE_SIZE }
      }),
  }
}
